import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {PagareCredentials, PaymentProvider} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';

interface DialogData {
  id: number;
  submitCredentialsFn: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider: PaymentProvider;
}

@Component({
  selector: 'app-pagare-credentials',
  templateUrl: './pagare-credentials.component.html',
  styleUrls: ['./pagare-credentials.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
  ],
})
export class PagareCredentialsComponent {
  form: FormGroup;
  submitting = false;
  id!: number;
  submitCredentialsFn!: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider!: PaymentProvider;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.submitCredentialsFn = this.context.data.submitCredentialsFn;
    this.provider = this.context.data.provider;

    this.form = this.fb.group({
      advanced: [false],
      sandbox: [false],
      accountId: ['', [Validators.required, Validators.minLength(1)]],
      tenantKey: [''],
      username: [''],
      password: [''],
      signingSecret: [''],
    });

    this.updateValidators();
    this.form.get('advanced')?.valueChanges.subscribe(() => {
      this.updateValidators();
    });
  }

  private updateValidators(): void {
    const advanced = this.form.get('advanced')?.value;
    const tenantKeyControl = this.form.get('tenantKey');
    const usernameControl = this.form.get('username');
    const passwordControl = this.form.get('password');

    if (advanced) {
      tenantKeyControl?.setValidators([
        Validators.required,
        Validators.minLength(1),
      ]);
      usernameControl?.setValidators([
        Validators.required,
        Validators.minLength(1),
      ]);
      passwordControl?.setValidators([
        Validators.required,
        Validators.minLength(1),
      ]);
    } else {
      tenantKeyControl?.clearValidators();
      usernameControl?.clearValidators();
      passwordControl?.clearValidators();
    }

    tenantKeyControl?.updateValueAndValidity({emitEvent: false});
    usernameControl?.updateValueAndValidity({emitEvent: false});
    passwordControl?.updateValueAndValidity({emitEvent: false});
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;

    const raw = this.form.value as {
      advanced: boolean;
      sandbox?: boolean;
      accountId: string;
      tenantKey: string;
      username: string;
      password: string;
      signingSecret: string;
    };

    const payload: PagareCredentials = {
      sandbox: raw.sandbox,
      accountId: raw.accountId.trim(),
      tenantKey: raw.tenantKey.trim(),
      username: raw.username.trim(),
      password: raw.password.trim(),
    };

    const signingSecret = raw.signingSecret?.trim();
    if (signingSecret) {
      payload.signingSecret = signingSecret;
    }

    let handledSuccess = false;
    const handleSuccess = () => {
      if (handledSuccess) return;
      handledSuccess = true;
      this.alerts
        .open(
          this.translate.instant(
            'features.integrations.credentials.success.message',
          ),
          {
            label: this.translate.instant(
              'features.integrations.credentials.success.title',
            ),
            appearance: 'success',
          },
        )
        .subscribe();
      this.context.completeWith(true);
    };

    this.submitCredentialsFn(this.provider, payload).subscribe({
      next: () => handleSuccess(),
      complete: () => handleSuccess(),
      error: (error: Error) => {
        console.error('Error creating API key:', error);
        this.alerts
          .open(
            this.translate.instant(
              'features.integrations.credentials.error.message',
            ),
            {
              label: this.translate.instant(
                'features.integrations.credentials.error.title',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
